import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, Check, Mic } from "lucide-react";
import { speak, speakPhoneme, preload } from "../../services/phonicsAudio";
import { sfx } from "../../services/soundfx";
import { buildOptions, buildLetterTiles, itemFor, shuffle as shuffleArr, cvcEmoji } from "../../data/phonicsContent";

function Prompt({ text, children, color = "#8B5CF6" }) {
  return (
    <div className="flex items-center gap-3 bg-white/95 rounded-3xl border-4 shadow-lg px-4 py-3 mt-1 max-w-lg" style={{ borderColor: color }}>
      <button
        type="button"
        data-testid="cave-replay-audio"
        onClick={() => { sfx.unlock(); speak(text); }}
        className="pop-btn no-select animate-pulse-glow inline-flex items-center justify-center rounded-full bg-amber-400 text-white border-4 border-white shadow-[0_5px_0_#D97706] active:shadow-none"
        style={{ width: 52, height: 52 }}
        aria-label="Play audio"
      >
        <Volume2 strokeWidth={2.5} className="w-6 h-6" />
      </button>
      <p className="font-display text-base sm:text-lg font-bold text-slate-800">{children}</p>
    </div>
  );
}

// 1) FEED: monster wants a sound; tap the picture that starts with it.
export function CaveFeed({ sound, allLetters, onDone }) {
  const [options] = useState(() => buildOptions(sound, allLetters, 3));
  const [firstTry, setFirstTry] = useState(true);
  const [wrong, setWrong] = useState(null);
  const [eaten, setEaten] = useState(false);

  useEffect(() => {
    preload([{ text: sound, kind: "phoneme" }]);
    const t = setTimeout(() => { speakPhoneme(sound); setTimeout(() => speak(`Which one starts with ${sound}?`), 700); }, 300);
    return () => clearTimeout(t);
  }, [sound]); // eslint-disable-line

  const tap = (opt) => {
    if (eaten) return;
    if (opt.letter === sound) {
      sfx.chomp();
      setEaten(true);
      speak("Yum! Correct!");
      setTimeout(() => onDone({ correct: firstTry }), 1100);
    } else {
      sfx.burp();
      setFirstTry(false);
      setWrong(opt.label);
      speak("Oops! Try again!");
      setTimeout(() => setWrong(null), 700);
    }
  };

  return (
    <div className="flex flex-col items-center gap-5">
      <Prompt text={`Feed me something that starts with ${sound}`} color="#8B5CF6">
        Feed the monster something that starts with <span className="text-violet-600 font-black">/{sound}/</span>!
      </Prompt>
      <motion.div animate={eaten ? { scale: [1, 1.2, 1] } : { y: [0, -6, 0] }} transition={eaten ? { duration: 0.5 } : { repeat: Infinity, duration: 2 }} className="text-7xl">
        {eaten ? "😋" : "👾"}
      </motion.div>
      <div className="flex flex-wrap items-center justify-center gap-4">
        {options.map((opt) => (
          <motion.button
            key={opt.label}
            data-testid={`cave-feed-${opt.letter}`}
            onClick={() => tap(opt)}
            animate={{ x: wrong === opt.label ? [0, -8, 8, 0] : 0 }}
            whileTap={{ scale: 0.9 }}
            className="pop-btn flex items-center justify-center rounded-3xl bg-white border-4 border-amber-200 shadow-[0_6px_0_#FBBF24] active:shadow-none"
            style={{ width: 96, height: 104 }}
          >
            <span className="text-5xl">{opt.emoji}</span>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// 2) LISTEN & MATCH: hear a sound, tap the matching letter.
export function CaveListen({ sound, allLetters, onDone }) {
  const [tiles] = useState(() => buildLetterTiles(sound, allLetters, 3));
  const [firstTry, setFirstTry] = useState(true);
  const [wrong, setWrong] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { speakPhoneme(sound); setTimeout(() => speak("Which letter makes that sound?"), 700); }, 300);
    return () => clearTimeout(t);
  }, [sound]); // eslint-disable-line

  const tap = (l) => {
    if (done) return;
    if (l === sound) {
      sfx.correct();
      setDone(true);
      speak("Yes! That is right!");
      setTimeout(() => onDone({ correct: firstTry }), 900);
    } else {
      sfx.wrong();
      setFirstTry(false);
      setWrong(l);
      speak("Listen again!");
      setTimeout(() => { setWrong(null); speakPhoneme(sound); }, 700);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6">
      <Prompt text="Which letter makes that sound?" color="#38BDF8">
        Listen 👂 then tap the letter you hear.
      </Prompt>
      <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 1.6 }} className="text-6xl">🔊</motion.div>
      <div className="flex flex-wrap items-center justify-center gap-4">
        {tiles.map((l) => (
          <motion.button
            key={l}
            data-testid={`cave-listen-${l}`}
            onClick={() => tap(l)}
            animate={{ x: wrong === l ? [0, -8, 8, 0] : 0 }}
            whileTap={{ scale: 0.9 }}
            className={`pop-btn flex items-center justify-center rounded-2xl font-display text-5xl font-black text-white border-4 border-white active:shadow-none ${done && l === sound ? "bg-green-500 shadow-[0_6px_0_#15803D]" : "bg-sky-500 shadow-[0_6px_0_#0284C7]"}`}
            style={{ width: 88, height: 88 }}
          >
            {l}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// 3) TRACE: drag a finger over the letter to write it.
export function CaveTrace({ sound, onDone }) {
  const letter = sound;
  const areaRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const drawing = useRef(false);
  const lastPt = useRef(null);
  const total = useRef(0);
  const [done, setDone] = useState(false);
  const TARGET = 520; // px of movement to complete

  useEffect(() => {
    const t = setTimeout(() => { speakPhoneme(sound); setTimeout(() => speak(`Trace the letter ${letter} with your finger!`), 700); }, 300);
    return () => clearTimeout(t);
  }, [sound]); // eslint-disable-line

  const start = (e) => { drawing.current = true; lastPt.current = pt(e); };
  const end = () => { drawing.current = false; lastPt.current = null; };
  const pt = (e) => {
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX, y: t.clientY };
  };
  const move = (e) => {
    if (!drawing.current || done) return;
    const p = pt(e);
    if (lastPt.current) {
      const dx = p.x - lastPt.current.x;
      const dy = p.y - lastPt.current.y;
      total.current += Math.sqrt(dx * dx + dy * dy);
      const pct = Math.min(1, total.current / TARGET);
      setProgress(pct);
      if (pct >= 1) {
        setDone(true);
        sfx.magic();
        speak("You wrote it! Great job!");
        setTimeout(() => onDone({ correct: true }), 1000);
      }
    }
    lastPt.current = p;
  };

  return (
    <div className="flex flex-col items-center gap-5">
      <Prompt text={`Trace the letter ${letter} with your finger`} color="#22C55E">
        Trace the letter with your finger ✍️
      </Prompt>
      <div
        ref={areaRef}
        data-testid="cave-trace-area"
        onMouseDown={start} onMouseUp={end} onMouseLeave={end} onMouseMove={move}
        onTouchStart={start} onTouchEnd={end} onTouchMove={move}
        className="relative rounded-3xl bg-white border-4 border-green-200 shadow-[0_8px_0_#86EFAC] flex items-center justify-center no-select touch-none"
        style={{ width: 240, height: 240 }}
      >
        <span
          className="font-display font-black select-none"
          style={{ fontSize: 180, color: done ? "#22C55E" : "#D1FAE5", WebkitTextStroke: done ? "0" : "4px #86EFAC", transition: "color 0.3s" }}
        >
          {letter}
        </span>
        {!done && <span className="absolute bottom-3 text-sm font-bold text-green-500">Draw over it!</span>}
      </div>
      {/* progress */}
      <div className="w-56 h-3 rounded-full bg-green-100 overflow-hidden">
        <motion.div className="h-full bg-green-500" style={{ width: `${Math.round(progress * 100)}%` }} />
      </div>
      {/* fallback tap for devices without drag */}
      {!done && progress < 0.15 && (
        <button
          data-testid="cave-trace-skip"
          onClick={() => { setDone(true); sfx.magic(); speak("Great job!"); setTimeout(() => onDone({ correct: true }), 800); }}
          className="text-xs font-bold text-green-600 underline"
        >
          Tap here if you traced it
        </button>
      )}
    </div>
  );
}

// 4) ECHO: hear the sound, say it out loud, tap to confirm.
export function CaveEcho({ sound, onDone }) {
  const item = itemFor(sound);
  const [phase, setPhase] = useState("listen"); // listen | count | say
  const [count, setCount] = useState(3);

  useEffect(() => {
    const t = setTimeout(() => { speakPhoneme(sound); setTimeout(() => { speak("Now YOU say it! Ready?"); setPhase("count"); }, 900); }, 300);
    return () => clearTimeout(t);
  }, [sound]); // eslint-disable-line

  useEffect(() => {
    if (phase !== "count") return;
    if (count <= 0) { setPhase("say"); speakPhoneme(sound); return; }
    sfx.tap();
    const t = setTimeout(() => setCount((c) => c - 1), 700);
    return () => clearTimeout(t);
  }, [phase, count]); // eslint-disable-line

  return (
    <div className="flex flex-col items-center gap-6">
      <Prompt text={`Say the ${sound} sound out loud!`} color="#EC4899">
        Say the <span className="text-pink-600 font-black">/{sound}/</span> sound out loud!
      </Prompt>
      <motion.div animate={{ scale: [1, 1.08, 1] }} transition={{ repeat: Infinity, duration: 1.5 }} className="text-7xl">{item.emoji}</motion.div>
      <div className="font-display text-8xl font-black text-pink-500">{sound}</div>

      {phase === "count" && <div className="font-display text-4xl font-black text-slate-500">{count > 0 ? count : "Go!"}</div>}

      {phase === "say" ? (
        <button
          data-testid="cave-echo-said"
          onClick={() => { sfx.correct(); speak("Awesome! You said it!"); setTimeout(() => onDone({ correct: true }), 800); }}
          className="pop-btn flex items-center gap-2 rounded-full bg-pink-500 text-white font-display text-xl font-bold px-7 py-4 border-4 border-white shadow-[0_6px_0_#BE185D] active:shadow-none"
        >
          <Check strokeWidth={3} /> I said it!
        </button>
      ) : (
        <div className="flex items-center gap-2 text-pink-400 font-bold"><Mic className="w-6 h-6" /> Get ready...</div>
      )}
    </div>
  );
}

// 5) BUILD (CVC): tap letters in order to make the word.
export function CaveBuild({ word, onDone }) {
  const letters = word.split("");
  const [tiles] = useState(() => word.split("").map((l, i) => ({ l, id: `${l}-${i}` })).sort(() => Math.random() - 0.5));
  const [placed, setPlaced] = useState([]);
  const [firstTry, setFirstTry] = useState(true);
  const [wrong, setWrong] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => speak(`Build the word ${word}!`), 400);
    return () => clearTimeout(t);
  }, [word]); // eslint-disable-line

  const placedIds = new Set(placed);
  const tap = (tile) => {
    const expected = letters[placed.length];
    if (tile.l === expected) {
      sfx.tap(); speakPhoneme(tile.l);
      const np = [...placed, tile.id];
      setPlaced(np);
      if (np.length === letters.length) {
        sfx.correct();
        setTimeout(() => { speak(word); setTimeout(() => onDone({ correct: firstTry }), 900); }, 300);
      }
    } else {
      sfx.wrong(); setFirstTry(false); setWrong(tile.id);
      speak("Try the next sound!");
      setTimeout(() => setWrong(null), 500);
    }
  };

  return (
    <div className="flex flex-col items-center gap-5">
      <Prompt text={`Build the word ${word}`} color="#F59E0B">
        Tap the letters in order to build the word!
      </Prompt>
      <div className="flex items-end gap-1 text-5xl">{cvcEmoji(word)}</div>
      <div className="flex gap-2">
        {letters.map((l, i) => (
          <div key={i} data-testid={`cave-build-slot-${i}`} className={`flex items-center justify-center rounded-xl border-4 font-display text-3xl font-black ${i < placed.length ? "bg-amber-500 text-white border-amber-200" : "border-dashed border-amber-300 text-transparent"}`} style={{ width: 56, height: 56 }}>
            {i < placed.length ? l : "?"}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap justify-center gap-3">
        {tiles.filter((t) => !placedIds.has(t.id)).map((t) => (
          <motion.button key={t.id} data-testid={`cave-build-${t.l}`} onClick={() => tap(t)} animate={{ x: wrong === t.id ? [0, -8, 8, 0] : 0 }} whileTap={{ scale: 0.9 }}
            className="pop-btn flex items-center justify-center rounded-2xl bg-amber-400 text-white font-display text-3xl font-black border-4 border-white shadow-[0_6px_0_#D97706] active:shadow-none" style={{ width: 60, height: 60 }}>
            {t.l}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// 6) READ & MATCH (CVC): hear a word, tap the matching picture.
export function CaveReadMatch({ word, words, onDone }) {
  const [options] = useState(() => {
    const pool = (words || []).filter((w) => w !== word);
    const picks = shuffleArr(pool).slice(0, 2);
    return shuffleArr([word, ...picks]);
  });
  const [firstTry, setFirstTry] = useState(true);
  const [done, setDone] = useState(false);
  const [wrong, setWrong] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { speak("Find the word"); setTimeout(() => speak(word), 800); }, 400);
    return () => clearTimeout(t);
  }, [word]); // eslint-disable-line

  const tap = (w) => {
    if (done) return;
    if (w === word) { sfx.correct(); setDone(true); speak("Yes!"); setTimeout(() => onDone({ correct: firstTry }), 900); }
    else { sfx.wrong(); setFirstTry(false); setWrong(w); speak(word); setTimeout(() => setWrong(null), 600); }
  };

  return (
    <div className="flex flex-col items-center gap-6">
      <Prompt text={`Find the word ${word}`} color="#38BDF8">Listen 👂 then tap the matching picture.</Prompt>
      <div className="flex flex-wrap justify-center gap-4">
        {options.map((w) => (
          <motion.button key={w} data-testid={`cave-readmatch-${w}`} onClick={() => tap(w)} animate={{ x: wrong === w ? [0, -8, 8, 0] : 0 }} whileTap={{ scale: 0.9 }}
            className="pop-btn flex flex-col items-center rounded-3xl bg-white border-4 border-sky-200 shadow-[0_6px_0_#7DD3FC] active:shadow-none p-3" style={{ width: 104 }}>
            <span className="text-5xl">{cvcEmoji(w)}</span>
            <span className="font-display font-bold text-slate-700 mt-1">{w}</span>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// 7) ECHO WORD: hear a word, say it, confirm.
export function CaveEchoWord({ word, onDone }) {
  useEffect(() => {
    const t = setTimeout(() => { speak(word); setTimeout(() => speak("Now YOU say it!"), 800); }, 400);
    return () => clearTimeout(t);
  }, [word]); // eslint-disable-line
  return (
    <div className="flex flex-col items-center gap-6">
      <Prompt text={`Say the word ${word} out loud`} color="#EC4899">Say the word out loud! 🗣️</Prompt>
      <div className="font-display text-7xl font-black text-pink-500">{word}</div>
      <button data-testid="cave-echoword-said" onClick={() => { sfx.correct(); speak("Awesome!"); setTimeout(() => onDone({ correct: true }), 700); }}
        className="pop-btn flex items-center gap-2 rounded-full bg-pink-500 text-white font-display text-xl font-bold px-7 py-4 border-4 border-white shadow-[0_6px_0_#BE185D] active:shadow-none">
        <Check strokeWidth={3} /> I said it!
      </button>
    </div>
  );
}

// 8) DIGRAPH: pick the two letters that complete the word.
export function CaveDigraph({ round, onDone }) {
  const [firstTry, setFirstTry] = useState(true);
  const [solved, setSolved] = useState(false);
  const [wrong, setWrong] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { speakPhoneme(round.target); setTimeout(() => speak(`Finish the word ${round.word}`), 800); }, 400);
    return () => clearTimeout(t);
  }, [round]); // eslint-disable-line

  const pick = (opt) => {
    if (solved) return;
    if (opt === round.target) { sfx.correct(); setSolved(true); speak(round.word); setTimeout(() => onDone({ correct: firstTry }), 1100); }
    else { sfx.wrong(); setFirstTry(false); setWrong(opt); setTimeout(() => setWrong(null), 500); }
  };

  return (
    <div className="flex flex-col items-center gap-6">
      <Prompt text={`Finish the word ${round.word}`} color="#14B8A6">Pick the two letters that make the sound!</Prompt>
      <div className="text-6xl">{round.emoji}</div>
      <div className="flex items-end gap-1">
        <span className="font-display text-5xl font-black text-slate-800">{round.before}</span>
        <div className="flex items-center justify-center rounded-xl border-4 font-display text-5xl font-black" style={{ minWidth: 90, height: 64, padding: "0 6px", background: solved ? "#14B8A6" : "transparent", color: solved ? "#fff" : "transparent", borderColor: "#5EEAD4", borderStyle: solved ? "solid" : "dashed" }}>
          {solved ? round.target : "__"}
        </div>
        <span className="font-display text-5xl font-black text-slate-800">{round.after}</span>
      </div>
      <div className="flex gap-3">
        {round.options.map((o) => (
          <motion.button key={o} data-testid={`cave-digraph-${o}`} onClick={() => pick(o)} animate={{ x: wrong === o ? [0, -8, 8, 0] : 0 }} whileTap={{ scale: 0.9 }}
            className="pop-btn flex items-center justify-center rounded-2xl bg-teal-500 text-white font-display text-3xl font-black border-4 border-white shadow-[0_6px_0_#0F766E] active:shadow-none" style={{ width: 84, height: 70 }}>
            {o}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// 9) SIGHT WORD: hear a sight word, tap the matching word.
export function CaveSight({ word, words, onDone }) {
  const [options] = useState(() => shuffleArr([word, ...shuffleArr((words || []).filter((w) => w !== word)).slice(0, 2)]));
  const [firstTry, setFirstTry] = useState(true);
  const [done, setDone] = useState(false);
  const [wrong, setWrong] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { speak("Pop the word"); setTimeout(() => speak(word), 800); }, 400);
    return () => clearTimeout(t);
  }, [word]); // eslint-disable-line

  const tap = (w) => {
    if (done) return;
    if (w === word) { sfx.pop(); setDone(true); speak("Yes!"); setTimeout(() => onDone({ correct: firstTry }), 900); }
    else { sfx.wrong(); setFirstTry(false); setWrong(w); speak(word); setTimeout(() => setWrong(null), 600); }
  };

  return (
    <div className="flex flex-col items-center gap-6">
      <Prompt text={`Find the word ${word}`} color="#8B5CF6">Listen 👂 then tap the word you hear.</Prompt>
      <div className="text-5xl">🫧</div>
      <div className="flex flex-wrap justify-center gap-3">
        {options.map((w) => (
          <motion.button key={w} data-testid={`cave-sight-${w}`} onClick={() => tap(w)} animate={{ x: wrong === w ? [0, -8, 8, 0] : 0 }} whileTap={{ scale: 0.9 }}
            className="pop-btn flex items-center justify-center rounded-2xl bg-violet-500 text-white font-display text-2xl font-black border-4 border-white shadow-[0_6px_0_#6D28D9] active:shadow-none px-5 py-3">
            {w}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

export const CAVE_GAMES = {
  feed: CaveFeed, listen: CaveListen, trace: CaveTrace, echo: CaveEcho,
  build: CaveBuild, readmatch: CaveReadMatch, echoword: CaveEchoWord,
  digraph: CaveDigraph, sight: CaveSight,
};
